import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Download, History, Search, XCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const SalesHistory = () => {
  const [sales, setSales] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('month'); // 'month', 'week', 'day', 'custom'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAnnulModalOpen, setIsAnnulModalOpen] = useState(false);
  const [saleToAnnul, setSaleToAnnul] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchSalesHistory();
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }
    }
  }, [filterType, selectedDate, startDate, endDate]);

  useEffect(() => {
    // Apply search filter
    if (searchQuery.trim()) {
      const filtered = sales.filter(sale => 
        sale.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sale.payment_method?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        formatCurrency(sale.total_amount).includes(searchQuery)
      );
      setFilteredSales(filtered);
    } else {
      setFilteredSales(sales);
    }
  }, [searchQuery, sales]);

  const fetchSalesHistory = async () => {
    setLoading(true);
    try {
      let start, end;
      const now = new Date();

      switch (filterType) {
        case 'month':
          // Get current month: first day to current day (inclusive)
          start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
          // End at the end of current day (not last day of month)
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;

        case 'week':
          // Get last 7 days
          start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;

        case 'day':
          // Get specific day - parse date string correctly to avoid timezone issues
          const [year, month, day] = selectedDate.split('-').map(Number);
          start = new Date(year, month - 1, day, 0, 0, 0, 0);
          end = new Date(year, month - 1, day, 23, 59, 59, 999);
          break;

        case 'custom':
          if (!startDate || !endDate) {
            setSales([]);
            setLoading(false);
            return;
          }
          // Parse dates correctly to avoid timezone issues
          const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
          const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
          start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
          end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
          break;

        default:
          start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
          end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }

      const response = await axios.get('/sales/history', {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          group_by: filterType === 'month' ? 'day' : null
        }
      });

      setSales(response.data);
      setFilteredSales(response.data);
    } catch (error) {
      toast.error('Error al cargar historial de ventas');
      console.error('Error fetching sales history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    // Handle different date formats
    if (!dateString) return 'N/A';
    
    // If it's a simple date string (YYYY-MM-DD), parse without timezone conversion
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // For ISO datetime strings, convert to local time
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: 'Efectivo',
      card: 'Tarjeta',
      transfer: 'Transferencia'
    };
    return labels[method] || method;
  };

  const getSaleId = (sale) => {
    // Use sale_number if available (new format)
    if (sale && sale.sale_number) {
      return sale.sale_number.toString();
    }
    // Fallback: use UUID slice (old format)
    if (sale && sale.id) {
      return sale.id.slice(0, 8);
    }
    // Last resort: use created_at timestamp as identifier
    if (sale && sale.created_at) {
      return new Date(sale.created_at).getTime().toString().slice(-8);
    }
    return 'N/A';
  };

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setIsDetailModalOpen(true);
  };

  const exportToExcel = () => {
    if (filteredSales.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    try {
      let exportData;

      if (filterType === 'month') {
        // Export daily aggregated data
        exportData = filteredSales.map(day => ({
          'Fecha': formatDate(day.date),
          'Cantidad de Ventas': day.sales_count,
          'Monto Total': formatCurrency(day.total_amount)
        }));
      } else {
        // Export individual sales - exclude voided sales
        const activeSales = filteredSales.filter(sale => sale.status !== 'voided');
        exportData = activeSales.map(sale => ({
          'Fecha': formatDate(sale.created_at),
          'No. Venta': getSaleId(sale),
          'Monto Total': formatCurrency(sale.total_amount),
          'Método de Pago': getPaymentMethodLabel(sale.payment_method),
          'Vendedor': sale.created_by
        }));
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Historial de Ventas');

      // Generate filename with date range
      const filename = `Historial_Ventas_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      toast.success('Archivo exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar archivo');
      console.error('Export error:', error);
    }
  };

  const openAnnulModal = (sale) => {
    setSaleToAnnul(sale);
    setIsAnnulModalOpen(true);
  };

  const canAnnulSale = (sale) => {
    // No se puede anular una venta ya anulada
    if (sale.status === 'voided') {
      return false;
    }
    
    // Si no hay usuario logueado, no mostrar botón
    if (!currentUser) {
      return false;
    }
    
    // Si es admin, puede anular cualquier venta
    if (currentUser.role === 'admin') {
      return true;
    }
    
    // Si es vendedor, solo puede anular sus propias ventas
    if (currentUser.role === 'vendedor' && sale.created_by === currentUser.username) {
      return true;
    }
    
    return false;
  };

  const handleAnnulSale = async () => {
    if (!saleToAnnul) return;

    setLoading(true);
    try {
      await axios.post(`/sales/${saleToAnnul.id}/void`);
      toast.success('Venta anulada exitosamente');
      setIsAnnulModalOpen(false);
      setSaleToAnnul(null);
      fetchSalesHistory(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al anular venta');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (sale) => {
    setSaleToDelete(sale);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;

    setLoading(true);
    try {
      await axios.delete(`/sales/${saleToDelete.id}`);
      toast.success('Venta eliminada permanentemente');
      setIsDeleteModalOpen(false);
      setSaleToDelete(null);
      fetchSalesHistory(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar venta');
    } finally {
      setLoading(false);
    }
  };


  const calculateTotals = () => {
    if (filterType === 'month') {
      const totalAmount = filteredSales.reduce((sum, day) => sum + day.total_amount, 0);
      const totalSales = filteredSales.reduce((sum, day) => sum + day.sales_count, 0);
      return { totalAmount, totalSales };
    } else if (filterType === 'week') {
      // Exclude voided sales from totals
      const activeSales = filteredSales.filter(sale => sale.status !== 'voided');
      const totalAmount = activeSales.reduce((sum, sale) => sum + sale.total_amount, 0);
      const totalSales = activeSales.length;
      return { totalAmount, totalSales };
    } else {
      // Exclude voided sales from totals
      const activeSales = filteredSales.filter(sale => sale.status !== 'voided');
      const totalAmount = activeSales.reduce((sum, sale) => sum + sale.total_amount, 0);
      const totalSales = activeSales.length;
      return { totalAmount, totalSales };
    }
  };

  const totals = calculateTotals();

  return (
    <div className="sales-history-page" data-testid="sales-history-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Historial de Ventas</h1>
          <p className="page-subtitle">Consulta y analiza las ventas por período</p>
        </div>
        <Button onClick={exportToExcel} disabled={filteredSales.length === 0}>
          <Download size={20} />
          Exportar a Excel
        </Button>
      </div>

      {/* Filters Section */}
      <Card className="filters-card">
        <div className="filters-container">
          <div className="filter-group">
            <Label>Filtrar por</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger data-testid="filter-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Por Mes (Totales Diarios)</SelectItem>
                <SelectItem value="week">Última Semana</SelectItem>
                <SelectItem value="day">Día Específico</SelectItem>
                <SelectItem value="custom">Rango Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filterType === 'day' && (
            <div className="filter-group">
              <Label>Seleccionar Día</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="day-picker"
              />
            </div>
          )}

          {filterType === 'custom' && (
            <>
              <div className="filter-group">
                <Label>Fecha Inicio</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-picker"
                />
              </div>
              <div className="filter-group">
                <Label>Fecha Fin</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="end-date-picker"
                />
              </div>
            </>
          )}

          <div className="filter-group search-group">
            <Label>Buscar</Label>
            <div className="search-box">
              <Search size={20} className="search-icon" />
              <Input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="summary-cards">
        <Card className="summary-card">
          <div className="summary-icon">
            <History size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Total de Ventas</p>
            <p className="summary-value">{totals.totalSales}</p>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-icon income">
            <Calendar size={24} />
          </div>
          <div className="summary-content">
            <p className="summary-label">Ingresos Totales</p>
            <p className="summary-value">{formatCurrency(totals.totalAmount)}</p>
          </div>
        </Card>
      </div>

      {/* Sales Table */}
      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando historial...</p>
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="empty-state">
          <History size={64} />
          <h3>No hay ventas en este período</h3>
          <p>Intenta seleccionar otro rango de fechas</p>
        </div>
      ) : (
        <Card className="table-card">
          <div className="table-container">
            {filterType === 'month' ? (
              // Monthly view: Show daily totals
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Cantidad de Ventas</th>
                    <th>Monto Total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((day) => (
                    <tr key={day.date} data-testid={`day-row-${day.date}`}>
                      <td className="date-cell">{formatDate(day.date)}</td>
                      <td className="count-cell">{day.sales_count}</td>
                      <td className="amount-cell">{formatCurrency(day.total_amount)}</td>
                      <td className="actions-cell">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(day)}
                          data-testid={`view-day-${day.date}`}
                        >
                          Ver Detalle
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // Week/Day/Custom view: Show individual sales
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>No. Venta</th>
                    <th>Estado</th>
                    <th>Monto Total</th>
                    <th>Método de Pago</th>
                    <th>Vendedor</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((sale, index) => (
                    <tr key={sale.id || `sale-${index}`} data-testid={`sale-row-${sale.id || index}`}>
                      <td className="date-cell">{formatDate(sale.created_at)}</td>
                      <td className="id-cell">#{getSaleId(sale)}</td>
                      <td className="status-cell">
                        {sale.status === 'voided' ? (
                          <span className="annulled-badge">Anulado</span>
                        ) : (
                          <span className="completed-badge">Completado</span>
                        )}
                      </td>
                      <td className="amount-cell">{formatCurrency(sale.total_amount)}</td>
                      <td className="payment-cell">
                        <span className="payment-badge">{getPaymentMethodLabel(sale.payment_method)}</span>
                      </td>
                      <td className="user-cell">{sale.created_by}</td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(sale)}
                            data-testid={`view-sale-${sale.id || getSaleId(sale)}`}
                          >
                            Ver Productos
                          </Button>
                          {canAnnulSale(sale) && (
                            <Button
                              size="sm"
                              onClick={() => openAnnulModal(sale)}
                              className="annul-button-warning"
                              data-testid={`annul-button-${sale.id}`}
                            >
                              <XCircle size={16} />
                              Anular
                            </Button>
                          )}
                          {sale.status === 'voided' && currentUser && currentUser.role === 'admin' && (
                            <Button
                              size="sm"
                              onClick={() => openDeleteModal(sale)}
                              className="delete-button-danger"
                              data-testid={`delete-button-${sale.id}`}
                            >
                              <Trash2 size={16} />
                              Eliminar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      )}

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="detail-modal">
          <DialogHeader>
            <DialogTitle>
              {filterType === 'month' && selectedSale?.date
                ? `Detalle del Día - ${formatDate(selectedSale.date)}`
                : `Detalle de Venta #${getSaleId(selectedSale)}`}
            </DialogTitle>
          </DialogHeader>

          <div className="modal-content">
            {filterType === 'month' && selectedSale?.sales ? (
              // Show all sales for that day
              <div className="day-sales-detail">
                <p className="detail-summary">
                  Total del día: {formatCurrency(selectedSale.total_amount)} ({selectedSale.sales_count} ventas)
                </p>
                <div className="sales-list">
                  {selectedSale.sales.map((sale, index) => (
                    <Card key={sale.id || `sale-${index}`} className="sale-detail-card">
                      <div className="sale-detail-header">
                        <span className="sale-detail-id">Venta #{getSaleId(sale)}</span>
                        <span className="sale-detail-total">{formatCurrency(sale.total_amount)}</span>
                      </div>
                      <div className="sale-detail-info">
                        <span className="payment-badge">{getPaymentMethodLabel(sale.payment_method)}</span>
                        <span className="sale-detail-user">Por: {sale.created_by}</span>
                      </div>
                      <div className="products-list">
                        <p className="products-title">Productos:</p>
                        {sale.items.map((item, idx) => (
                          <div key={idx} className="product-item">
                            <span className="product-name">{item.product_name}</span>
                            <span className="product-quantity">x{item.quantity}</span>
                            <span className="product-price">{formatCurrency(item.total)}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ) : selectedSale?.items ? (
              // Show products for a single sale
              <div className="products-detail">
                <div className="sale-info">
                  <div className="info-row">
                    <span className="info-label">Método de Pago:</span>
                    <span className="payment-badge">{getPaymentMethodLabel(selectedSale.payment_method)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Vendedor:</span>
                    <span className="info-value">{selectedSale.created_by}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Fecha:</span>
                    <span className="info-value">{formatDate(selectedSale.created_at)}</span>
                  </div>
                </div>

                <div className="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSale.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.product_name}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.unit_price)}</td>
                          <td className="total-cell">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" className="footer-label">Total:</td>
                        <td className="footer-total">{formatCurrency(selectedSale.total_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Annul Sale Confirmation Modal */}
      <Dialog open={isAnnulModalOpen} onOpenChange={setIsAnnulModalOpen}>
        <DialogContent className="annul-modal">
          <DialogHeader>
            <DialogTitle>Anular Venta</DialogTitle>
          </DialogHeader>
          <div className="annul-modal-content">
            <p className="annul-warning-text">
              ¿Deseas anular esta venta? Esta acción no se puede deshacer.
            </p>
            {saleToAnnul && (
              <div className="sale-info-box">
                <p><strong>Venta:</strong> #{getSaleId(saleToAnnul)}</p>
                <p><strong>Monto:</strong> {formatCurrency(saleToAnnul.total_amount)}</p>
                <p><strong>Fecha:</strong> {formatDate(saleToAnnul.created_at)}</p>
                <p><strong>Vendedor:</strong> {saleToAnnul.created_by}</p>
              </div>
            )}
            <div className="annul-modal-actions">
              <Button 
                variant="outline" 
                onClick={() => setIsAnnulModalOpen(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                className="confirm-annul-button"
                onClick={handleAnnulSale}
                disabled={loading}
                data-testid="confirm-annul-btn"
              >
                {loading ? 'Anulando...' : 'Confirmar Anulación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Voided Sale Confirmation Modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="delete-modal">
          <DialogHeader>
            <DialogTitle>Eliminar Venta Anulada</DialogTitle>
          </DialogHeader>
          <div className="delete-modal-content">
            <p className="delete-warning-text">
              ⚠️ ¿Deseas eliminar permanentemente esta venta anulada? Esta acción no se puede deshacer.
            </p>
            {saleToDelete && (
              <div className="sale-info-box">
                <p><strong>Venta:</strong> #{getSaleId(saleToDelete)}</p>
                <p><strong>Monto:</strong> {formatCurrency(saleToDelete.total_amount)}</p>
                <p><strong>Fecha:</strong> {formatDate(saleToDelete.created_at)}</p>
                <p><strong>Vendedor:</strong> {saleToDelete.created_by}</p>
              </div>
            )}
            <div className="delete-modal-actions">
              <Button 
                variant="outline" 
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                className="confirm-delete-button"
                onClick={handleDeleteSale}
                disabled={loading}
                data-testid="confirm-delete-btn"
              >
                {loading ? 'Eliminando...' : 'Confirmar Eliminación'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <style jsx>{`
        .sales-history-page {
          max-width: 1400px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .filters-card {
          padding: 1.5rem;
          margin-bottom: 2rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .filters-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          align-items: end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-group {
          grid-column: span 1;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .summary-card {
          padding: 1.5rem;
          display: flex;
          gap: 1.5rem;
          align-items: center;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .summary-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .summary-icon.income {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .summary-content {
          flex: 1;
        }

        .summary-label {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.5rem;
        }

        .summary-value {
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e293b;
        }

        .table-card {
          padding: 0;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .table-container {
          overflow-x: auto;
        }

        .sales-table {
          width: 100%;
          border-collapse: collapse;
        }

        .sales-table thead {
          background: #f8fafc;
        }

        .sales-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 700;
          font-size: 0.875rem;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 2px solid #e2e8f0;
        }

        .sales-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .sales-table tbody tr:hover {
          background: #f8fafc;
        }

        .date-cell {
          font-weight: 600;
          color: #1e293b;
        }

        .id-cell {
          font-family: monospace;
          color: #64748b;
        }

        .count-cell {
          font-weight: 600;
          color: #3b82f6;
        }

        .amount-cell {
          font-weight: 700;
          color: #10b981;
          font-size: 1.0625rem;
        }

        .payment-cell {
          text-align: center;
        }

        .payment-badge {
          padding: 0.25rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          display: inline-block;
        }

        .user-cell {
          color: #64748b;
        }

        .actions-cell {
          text-align: right;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
          align-items: center;
        }

        .annul-button {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #ef4444;
          color: white;
        }

        .annul-button:hover {
          background: #dc2626;
        }

        .status-cell {
          text-align: center;
        }

        .annulled-badge {
          padding: 0.25rem 0.75rem;
          background: #fee2e2;
          color: #dc2626;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          display: inline-block;
        }

        .completed-badge {
          padding: 0.25rem 0.75rem;
          background: #d1fae5;
          color: #047857;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          display: inline-block;
        }

        .annul-button-warning {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .annul-button-warning:hover {
          background: #d97706;
        }


        .delete-button-danger {
          font-size: 0.875rem;
          padding: 0.4rem 0.8rem;
          height: auto;
          background: #dc2626;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          transition: background 0.2s;
        }

        .delete-button-danger:hover {
          background: #991b1b;
        }


        .loading-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #e2e8f0;
          border-top-color: #10b981;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .detail-modal {
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-content {
          margin-top: 1rem;
        }

        .detail-summary {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .sales-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .sale-detail-card {
          padding: 1.5rem;
          border: 1px solid #e2e8f0;
        }

        .sale-detail-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .sale-detail-id {
          font-weight: 700;
          color: #1e293b;
          font-family: monospace;
        }

        .sale-detail-total {
          font-size: 1.25rem;
          font-weight: 800;
          color: #10b981;
        }

        .sale-detail-info {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1rem;
        }

        .sale-detail-user {
          font-size: 0.875rem;
          color: #64748b;
        }

        .products-list {
          background: #f8fafc;
          padding: 1rem;
          border-radius: 8px;
        }

        .products-title {
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.75rem;
          font-size: 0.875rem;
        }

        .product-item {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 1rem;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .product-item:last-child {
          border-bottom: none;
        }

        .product-name {
          font-weight: 500;
          color: #1e293b;
        }

        .product-quantity {
          color: #64748b;
        }

        .product-price {
          font-weight: 700;
          color: #10b981;
        }

        .products-detail {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .sale-info {
          background: #f8fafc;
          padding: 1.5rem;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .info-label {
          font-weight: 600;
          color: #475569;
          font-size: 0.9375rem;
        }

        .info-value {
          color: #1e293b;
          font-weight: 500;
        }

        .products-table {
          overflow-x: auto;
        }

        .products-table table {
          width: 100%;
          border-collapse: collapse;
        }

        .products-table thead {
          background: #f8fafc;
        }

        .products-table th {
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
          color: #475569;
          border-bottom: 2px solid #e2e8f0;
        }

        .products-table td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e2e8f0;
          color: #1e293b;
        }

        .products-table .total-cell {
          font-weight: 700;
          color: #10b981;
        }

        .products-table tfoot {
          background: #f8fafc;
          font-weight: 700;
        }

        .footer-label {
          text-align: right;
          color: #1e293b;
          font-size: 1.0625rem;
        }

        .footer-total {
          color: #10b981;
          font-size: 1.25rem;
        }

        /* Annul Modal Styles */
        .annul-modal {
          max-width: 500px;
          z-index: 9999;
        }

        .annul-modal-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .annul-warning-text {
          font-size: 1rem;
          color: #475569;
          text-align: center;
          margin: 0;
        }

        .sale-info-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .sale-info-box p {
          margin: 0;
          font-size: 0.9375rem;
          color: #475569;
        }

        .sale-info-box strong {
          color: #1e293b;
        }

        .annul-modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .confirm-annul-button {
          background: #f59e0b;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }

        .confirm-annul-button:hover {
          background: #d97706;
        }

        .confirm-annul-button:disabled {
          background: #fbbf24;
          opacity: 0.5;
          cursor: not-allowed;
        }


        /* Delete Modal Styles */
        .delete-modal {
          max-width: 500px;
          z-index: 9999;
        }

        .delete-modal-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 1rem;
        }

        .delete-warning-text {
          font-size: 1rem;
          color: #dc2626;
          text-align: center;
          margin: 0;
          font-weight: 600;
          padding: 1rem;
          background: #fef2f2;
          border: 2px solid #fecaca;
          border-radius: 8px;
        }

        .delete-modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
        }

        .confirm-delete-button {
          background: #dc2626;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: background 0.2s;
        }

        .confirm-delete-button:hover {
          background: #991b1b;
        }

        .confirm-delete-button:disabled {
          background: #ef4444;
          opacity: 0.5;
          cursor: not-allowed;
        }


        @media (max-width: 768px) {
          .filters-container {
            grid-template-columns: 1fr;
          }

          .summary-cards {
            grid-template-columns: 1fr;
          }

          .sales-table {
            font-size: 0.875rem;
          }

          .sales-table th,
          .sales-table td {
            padding: 0.75rem 0.5rem;
          }

          .product-item {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesHistory;
